// Shared "active staff" helpers for assign/reassign pickers.
//
// When a person leaves we set Staff.FlagActive = 0 but keep their Staff row so
// historical assignments (jobs, tasks, vehicles, notes) still resolve to a
// name. See STAFF_LEAVER_HIDE_PLAN.md.
//
// The rule for any "assign a staff member" dropdown is:
//   - resolve the CURRENT value against the FULL list (so a task already on a
//     leaver still shows their name), but
//   - only offer filterActiveStaff(list) as the selectable OPTIONS, so leavers
//     can't be picked for new work and can't be re-selected once moved off.
//
// The backend /staff/list still returns everyone (leavers included); this keeps
// the filtering on the client so the same full list can serve both display and
// picker needs without a second request.

// FlagActive comes back from the API as the string "1"/"0" (TINYINT), so
// compare as string and treat a missing flag as active to stay safe on rows
// that predate the column.
export const isActiveStaff = (s) =>
  s == null ? false : s.FlagActive == null || String(s.FlagActive) === "1";

/**
 * A-Z by display name (Task 24458).
 *
 * Staff pickers were rendering in whatever order /staff/list returned, which is
 * insertion order - so finding a name meant reading the whole list. Different
 * pickers label people differently (some FullName, some FirstName + LastName),
 * so sort on whichever is available and fall back to the id.
 *
 * localeCompare with sensitivity "base" so "de Vries" and "De Vries" sit
 * together rather than splitting on case, and numeric so "Team 2" precedes
 * "Team 10".
 */
export const staffDisplayName = (s) =>
  (s?.FullName ||
    [s?.FirstName, s?.LastName].filter(Boolean).join(" ") ||
    "").trim();

export const sortStaffByName = (list) =>
  [...(list || [])].sort((a, b) => {
    const nameA = staffDisplayName(a);
    const nameB = staffDisplayName(b);
    // Anyone with no name at all goes last rather than jumping to the top.
    if (!nameA && !nameB) return 0;
    if (!nameA) return 1;
    if (!nameB) return -1;
    return nameA.localeCompare(nameB, undefined, {
      sensitivity: "base",
      numeric: true,
    });
  });

/**
 * Active-only staff for picker OPTIONS.
 *
 * Returns them A-Z (Task 24458). Sorting here rather than at each call site
 * means every picker that already funnels through this helper is ordered
 * without touching it - and a new picker gets it by default.
 *
 * @param {Array}  list     full staff list (may include leavers)
 * @param {Array}  keepIds  ids to keep even if inactive — pass the currently
 *                          selected id(s) for pickers (MUI/react-select) that
 *                          render the selected label FROM the options, so a
 *                          current leaver assignment still shows. Omit for
 *                          pickers like StaffSelect that render the current
 *                          value separately.
 */
export const filterActiveStaff = (list, keepIds = []) => {
  const keep = new Set(
    (Array.isArray(keepIds) ? keepIds : [keepIds])
      .filter((v) => v !== null && v !== undefined && v !== "")
      .map(String)
  );
  return sortStaffByName(
    (list || []).filter((s) => isActiveStaff(s) || keep.has(String(s?.Id)))
  );
};

export default filterActiveStaff;
